import * as XLSX from "xlsx";

// Keys that hold dates (exchange_date, sale_saleDate, payment_date, ...createdAt)
const DATE_KEY = /(date|createdat)$/i;

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString("en-IN");
};

// Clean values: undefined/null → "", ISO dates → dd/mm/yyyy
const cleanRows = (rows) =>
  rows.map((r, i) => {
    const out = { sr_no: i + 1 };
    Object.keys(r).forEach((k) => {
      let v = r[k];
      if (v === undefined || v === null) v = "";
      else if (DATE_KEY.test(k)) v = fmtDate(v);
      out[k] = v;
    });
    return out;
  });

// Auto column widths
const autoWidth = (data) => {
  if (!data.length) return [];
  return Object.keys(data[0]).map((key) => {
    const max = data.reduce(
      (m, row) => Math.max(m, String(row[key] ?? "").length),
      key.length
    );
    return { wch: Math.min(Math.max(max + 2, 10), 60) };
  });
};

const makeSheet = (data) => {
  const ws = XLSX.utils.json_to_sheet(data);
  ws["!cols"] = autoWidth(data);
  return ws;
};

// Reference columns repeated on every split sheet so rows can be matched
const REF_KEYS = [
  "sr_no",
  "exchange_slNo",
  "exchange_model",
  "exchange_registrationNumber",
  "sale_invoiceNo",
];

// Pick reference columns + every column starting with one of the prefixes
const pickByPrefix = (data, prefixes) =>
  data.map((row) => {
    const out = {};
    REF_KEYS.forEach((k) => { if (k in row) out[k] = row[k]; });
    Object.keys(row).forEach((k) => {
      if (prefixes.some((p) => k.startsWith(p))) out[k] = row[k];
    });
    return out;
  });

// One row per due installment (exchange due + normal sale due)
const buildInstallments = (bikes = []) => {
  const list = [];
  bikes.forEach((b, i) => {
    const s     = b.sale || {};
    const first = (s.exchange?.payments || [])[0] || {};

    const base = {
      sr_no:                       i + 1,
      exchange_slNo:               b.slNo,
      exchange_model:              b.model,
      exchange_registrationNumber: b.registrationNumber,
      sale_invoiceNo:              s.invoiceNo,
      sale_customerName:           s.customerName,
    };

    (first.dueExchange?.payments || []).forEach((p) => {
      list.push({
        ...base,
        due_type: "EXCHANGE DUE",
        amount:   p.amount ?? "",
        date:     fmtDate(p.date),
        note:     p.note || "",
      });
    });

    (s.due?.payments || []).forEach((p) => {
      list.push({
        ...base,
        due_type: "SALE DUE",
        amount:   p.amount ?? "",
        date:     fmtDate(p.date),
        note:     p.note || "",
      });
    });
  });
  return list;
};

/**
 * res = response of exchangeBikeService.getExchangeBikesFullDetail()
 * { rows: [...flat], bikes: [...nested] }
 */
export const exportExchangeBikesExcel = (res) => {
  const rows = res?.rows || [];
  if (!rows.length) throw new Error("No exchange bike data to export");

  const data = cleanRows(rows);
  const wb   = XLSX.utils.book_new();

  // 1) Everything in one sheet
  XLSX.utils.book_append_sheet(wb, makeSheet(data), "Full Detail");

  // 2) Split sheets
  XLSX.utils.book_append_sheet(wb, makeSheet(pickByPrefix(data, ["exchange_"])), "Exchange Bike");
  XLSX.utils.book_append_sheet(wb, makeSheet(pickByPrefix(data, ["parent_"])),   "Parent Bike");
  XLSX.utils.book_append_sheet(wb, makeSheet(pickByPrefix(data, ["sale_"])),     "Sale");
  XLSX.utils.book_append_sheet(
    wb,
    makeSheet(pickByPrefix(data, ["payment_", "dueExchange_"])),
    "Exchange Payment"
  );

  // 3) Due installments (one row per installment)
  const installments = buildInstallments(res?.bikes);
  if (installments.length) {
    XLSX.utils.book_append_sheet(wb, makeSheet(installments), "Due Installments");
  }

  const today = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `Exchange_Bikes_${today}.xlsx`);
};